import { useEffect, useMemo, useState } from 'react';
import Btn from './ui/Btn';
import { ApiError, api } from '../lib/api';
import { toast } from '../stores/toaster';
import type { Partie } from '../lib/types';

/**
 * Onglet « Contradictions » v2 (Sprint D §D.2 refonte).
 *
 * Un seul bouton « Scanner le dossier » qui lance un scan global :
 * Sonnet identifie toutes les contradictions, intra-personne ET
 * inter-personnes, en une passe. Les cards portent un tag
 * « Intra <partie> » ou « Inter : <A> ↔ <B> ».
 */

type Contradiction = {
  id: string;
  dossier_id: string;
  kind: 'intra' | 'inter' | 'unilateral' | 'unknown';
  partie_id: string | null;
  partie_b_id: string | null;
  source_a_pdf_id: string;
  source_a_page: number;
  source_a_quote: string;
  source_b_pdf_id: string;
  source_b_page: number;
  source_b_quote: string;
  contradiction_type: string;
  description: string;
  severity: 'critical' | 'major' | 'minor';
  confidence: number;
  status: 'unverified' | 'verified_true_positive' | 'verified_false_positive' | 'dismissed';
  created_at: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
};

type ScanResult = {
  id: string;
  cost_eur: number;
  findings_count: number;
  finished_at: string | null;
  error: string | null;
};

const TYPE_LABELS: Record<string, string> = {
  date: 'Date',
  lieu: 'Lieu',
  montant: 'Montant',
  identite: 'Identité',
  fait: 'Fait',
  temporalite: 'Temporalité',
  autre: 'Autre',
};

const SEVERITY_RANK: Record<string, number> = { critical: 0, major: 1, minor: 2 };

const SEVERITY_BADGE: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 border-red-300',
  major: 'bg-orange-100 text-orange-800 border-orange-300',
  minor: 'bg-amber-50 text-amber-800 border-amber-200',
};

const SEVERITY_LABEL: Record<string, string> = {
  critical: 'Critique',
  major: 'Majeure',
  minor: 'Mineure',
};

const STATUS_LABEL: Record<string, string> = {
  unverified: 'Non revu',
  verified_true_positive: 'Confirmée',
  verified_false_positive: 'Faux positif',
  dismissed: 'Écartée',
};

type Props = {
  dossierId: string;
  parties: Partie[];
  onOpenPdf?: (pdfId: string, page: number) => void;
};

export default function ContradictionsTab({ dossierId, parties, onOpenPdf }: Props) {
  const [contradictions, setContradictions] = useState<Contradiction[]>([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [includeDismissed, setIncludeDismissed] = useState(false);
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');

  const partieById = useMemo(() => {
    const m = new Map<string, Partie>();
    for (const p of parties) m.set(p.id, p);
    return m;
  }, [parties]);

  const partieLabel = (id: string | null): string => {
    if (!id) return '?';
    const p = partieById.get(id);
    if (!p) return '?';
    return `${p.prenom ?? ''} ${p.nom ?? ''}`.trim() || '?';
  };

  const load = async () => {
    setLoading(true);
    try {
      const rows = await api.get<Contradiction[]>(
        `/dossiers/${dossierId}/contradictions${includeDismissed ? '?include_dismissed=1' : ''}`
      );
      setContradictions(rows);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossierId, includeDismissed]);

  const triggerScan = async () => {
    if (
      !window.confirm(
        'Lancer un scan complet du dossier ?\n\n' +
          'Sonnet analyse toutes les auditions et identifie les contradictions ' +
          'intra-personne (un témoin/suspect qui se contredit) ET inter-personnes ' +
          '(deux personnes qui se contredisent sur un même fait). Coût attendu : ~0.50–2 €.'
      )
    )
      return;
    setScanning(true);
    try {
      const run = await api.post<ScanResult>(`/dossiers/${dossierId}/contradictions-scan`, {});
      if (run.error) {
        toast.error(`Scan terminé avec erreur : ${run.error}`);
      } else if (run.findings_count === 0) {
        toast.success('Aucune contradiction détectée dans le dossier.');
      } else {
        toast.success(
          `${run.findings_count} contradiction${run.findings_count > 1 ? 's' : ''} détectée${
            run.findings_count > 1 ? 's' : ''
          } (${run.cost_eur.toFixed(4)} €).`
        );
      }
      await load();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Échec du scan'
      );
    } finally {
      setScanning(false);
    }
  };

  const updateStatus = async (
    c: Contradiction,
    newStatus: Contradiction['status'],
    notes?: string
  ) => {
    try {
      await api.patch(`/contradictions/${c.id}`, {
        status: newStatus,
        review_notes: notes ?? c.review_notes ?? '',
      });
      await load();
      setReviewingId(null);
      setReviewNotes('');
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const sorted = useMemo(() => {
    const arr = contradictions.slice();
    arr.sort((a, b) => {
      const sa = SEVERITY_RANK[a.severity] ?? 99;
      const sb = SEVERITY_RANK[b.severity] ?? 99;
      if (sa !== sb) return sa - sb;
      return b.confidence - a.confidence;
    });
    return arr;
  }, [contradictions]);

  const kindLabel = (c: Contradiction): string => {
    if (c.kind === 'intra') return `Intra · ${partieLabel(c.partie_id)}`;
    if (c.kind === 'inter')
      return `Inter · ${partieLabel(c.partie_id)} ↔ ${partieLabel(c.partie_b_id)}`;
    if (c.kind === 'unilateral') return `Personne : ${partieLabel(c.partie_id || c.partie_b_id)}`;
    return 'Non rattachée';
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-rule bg-paper-card p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h2 className="font-serif text-[15px] font-medium text-text-primary">
              🔀 Contradictions
            </h2>
            <p className="mt-1 text-[12px] text-text-tertiary">
              Sonnet 4.6 scanne toutes les auditions du dossier et liste les contradictions
              factuelles, qu'elles soient{' '}
              <strong>entre les déclarations d'une même personne</strong> (témoin / suspect /
              victime qui se contredit) ou <strong>entre personnes différentes</strong> (deux
              versions incompatibles d'un même fait). À <strong>vérifier humainement</strong>.
            </p>
          </div>
          <Btn variant="primary" size="sm" onClick={() => void triggerScan()} disabled={scanning}>
            {scanning ? 'Scan en cours…' : '↻ Scanner le dossier'}
          </Btn>
        </div>
        <label className="mt-3 flex items-center gap-1 text-[11px] text-text-secondary">
          <input
            type="checkbox"
            checked={includeDismissed}
            onChange={(e) => setIncludeDismissed(e.target.checked)}
          />
          Inclure les contradictions écartées
        </label>
      </div>

      {loading ? (
        <p className="text-[12px] text-text-tertiary">Chargement…</p>
      ) : sorted.length === 0 ? (
        <p className="rounded-lg border border-dashed border-rule bg-paper-elevated p-6 text-center text-[13px] text-text-tertiary">
          Aucune contradiction. Lance un scan pour analyser le dossier.
        </p>
      ) : (
        <ul className="space-y-3">
          {sorted.map((c) => (
            <li
              key={c.id}
              className={`rounded-md border border-rule bg-paper-elevated p-3 ${
                c.status === 'dismissed' || c.status === 'verified_false_positive'
                  ? 'opacity-50'
                  : ''
              }`}
            >
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span
                  className={`rounded-sm border px-1.5 py-0.5 text-[10px] font-medium uppercase ${
                    SEVERITY_BADGE[c.severity] ?? ''
                  }`}
                >
                  {SEVERITY_LABEL[c.severity] ?? c.severity}
                </span>
                <span className="rounded-sm bg-paper-card px-1.5 py-0.5 text-[10px] font-medium text-text-secondary">
                  {TYPE_LABELS[c.contradiction_type] ?? c.contradiction_type}
                </span>
                <span className="rounded-pill bg-paper-subtle px-2 py-[1px] text-[10px] font-medium text-text-secondary">
                  {kindLabel(c)}
                </span>
                <span className="text-[10px] text-text-tertiary">
                  {(c.confidence * 100).toFixed(0)}%
                </span>
                <span className="ml-auto text-[10px] uppercase tracking-[1px] text-text-tertiary">
                  {STATUS_LABEL[c.status] ?? c.status}
                </span>
              </div>
              <p className="text-[12px] text-text-primary">{c.description}</p>
              <div className="mt-2 grid gap-2 md:grid-cols-2">
                <blockquote
                  className="cursor-pointer rounded-sm border-l-2 border-blue-400 bg-blue-50/40 px-3 py-2 text-[11px] text-text-secondary hover:bg-blue-50"
                  onClick={() => onOpenPdf?.(c.source_a_pdf_id, c.source_a_page)}
                  title="Ouvrir source A"
                >
                  <div className="mb-1 text-[9px] font-medium uppercase tracking-[1px] text-blue-700">
                    Source A · page {c.source_a_page}
                    {c.kind === 'inter' && c.partie_id && (
                      <span className="ml-1 normal-case">· {partieLabel(c.partie_id)}</span>
                    )}
                  </div>
                  « {c.source_a_quote} »
                </blockquote>
                <blockquote
                  className="cursor-pointer rounded-sm border-l-2 border-purple-400 bg-purple-50/40 px-3 py-2 text-[11px] text-text-secondary hover:bg-purple-50"
                  onClick={() => onOpenPdf?.(c.source_b_pdf_id, c.source_b_page)}
                  title="Ouvrir source B"
                >
                  <div className="mb-1 text-[9px] font-medium uppercase tracking-[1px] text-purple-700">
                    Source B · page {c.source_b_page}
                    {c.kind === 'inter' && c.partie_b_id && (
                      <span className="ml-1 normal-case">· {partieLabel(c.partie_b_id)}</span>
                    )}
                  </div>
                  « {c.source_b_quote} »
                </blockquote>
              </div>

              {reviewingId === c.id ? (
                <div className="mt-3 space-y-2">
                  <textarea
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="Notes de revue (optionnel)"
                    className="w-full rounded-md border border-rule-strong bg-paper-card p-2 text-[12px]"
                    rows={2}
                  />
                  <div className="flex flex-wrap gap-1.5">
                    <Btn
                      variant="primary"
                      size="sm"
                      onClick={() =>
                        void updateStatus(c, 'verified_true_positive', reviewNotes || undefined)
                      }
                    >
                      ✓ Confirmer
                    </Btn>
                    <Btn
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        void updateStatus(c, 'verified_false_positive', reviewNotes || undefined)
                      }
                    >
                      ✗ Faux positif
                    </Btn>
                    <Btn
                      variant="ghost"
                      size="sm"
                      onClick={() => void updateStatus(c, 'dismissed', reviewNotes || undefined)}
                    >
                      Écarter
                    </Btn>
                    <Btn
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setReviewingId(null);
                        setReviewNotes('');
                      }}
                    >
                      Annuler
                    </Btn>
                  </div>
                </div>
              ) : (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <Btn
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setReviewingId(c.id);
                      setReviewNotes(c.review_notes ?? '');
                    }}
                  >
                    Revoir
                  </Btn>
                  {c.review_notes && (
                    <span className="text-[11px] italic text-text-tertiary">
                      « {c.review_notes} »
                    </span>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
